import { createSelector } from '@reduxjs/toolkit';

import { Distribution, Load } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { LoadVisualLoad, prepareLoadForDrawing } from '../../library/loadProjectionUtilities';
import { poundPerFootToInchScalar, poundToInchScalar } from '../../library/unitScalarsForVisuals';
import { RootState } from '..';
import { getAnalogModelForActiveComponentDesign } from './getAnalogModelForActiveComponentDesignSelector';
import { getAllLoadsForActiveComponentDesign } from './getLoadCasesForActiveComponentDesignSelector';

const minLoadVisualRatio = 1 / 144; // 2" on a 24' truss
const maxLoadVisualRatio = 1 / 6; // 4' on a 24' truss

export type LoadVisualScaler = (unscaledValue: number, distribution: Distribution) => number;

const defaultLoadVisualScaler: LoadVisualScaler = (unscaledValue: number, distribution: Distribution) => {
  const scalar =
    distribution === 'Concentrated'
      ? poundToInchScalar
      : distribution === 'Distributed'
        ? poundPerFootToInchScalar
        : assertExhaustiveSwitch(distribution);
  return unscaledValue * scalar;
};

export const getActiveComponentDesignLoadVisualScaler = createSelector(
  getAnalogModelForActiveComponentDesign,
  getAllLoadsForActiveComponentDesign,
  (state: RootState) => state.visualOptions.showLoadsUnprojected,
  (analogModel, allLoads, showLoadsUnprojected) => {
    if (!analogModel || !allLoads.length) return defaultLoadVisualScaler;
    const jointXs = analogModel.analogJoints.map(({ x }) => x);
    const span = Math.max(...jointXs) - Math.min(...jointXs);
    if (!span) return defaultLoadVisualScaler;

    return calculateLoadVisualScaler(allLoads, span, showLoadsUnprojected);
  },
);

const calculateLoadVisualScaler = (loads: readonly Load[], span: number, unproject: boolean): LoadVisualScaler => {
  const minLoadVisualSize = span * minLoadVisualRatio;
  const deltaLoadVisualSize = span * maxLoadVisualRatio - minLoadVisualSize;

  const allLoadMagnitudes = loads
    .map(prepareLoadForDrawing(unproject))
    .map(({ load }) => load)
    .flatMap(loadMagnitudes);

  const minLoadMagnitude = Math.min(...allLoadMagnitudes);
  const deltaLoadMagnitude = Math.max(...allLoadMagnitudes) - minLoadMagnitude;
  const magnitudeToVisualSizeRatio = deltaLoadVisualSize / deltaLoadMagnitude;
  return (unscaledValue: number, distribution: Distribution) => {
    const scalar =
      distribution === 'Concentrated'
        ? poundToInchScalar
        : distribution === 'Distributed'
          ? poundPerFootToInchScalar
          : assertExhaustiveSwitch(distribution);
    return (
      ((Math.abs(unscaledValue * scalar) - minLoadMagnitude) * magnitudeToVisualSizeRatio + minLoadVisualSize) *
      Math.sign(unscaledValue)
    );
  };
};

const loadMagnitudes = (load: LoadVisualLoad) => {
  switch (load.distribution) {
    case 'Concentrated':
      return [Math.abs(load.startMagnitude) * poundToInchScalar];
    case 'Distributed':
      return [
        Math.abs(load.startMagnitude) * poundPerFootToInchScalar,
        Math.abs(load.endMagnitude) * poundPerFootToInchScalar,
      ];
    default:
      assertExhaustiveSwitch(load);
  }
};
